import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { ParsedToken, QueryBuilderChangeEvent } from '../../../types/QueryBuilder';
import type { ActivePopover, FieldOwner, QueryBuilderContextValue } from '../context';
import { parseQuery } from '../parseQuery';
import type { ClipboardDialect } from '../utils';
import {
  dropDanglingLogicNots,
  parseClipboardQuery,
  removeToken,
  replaceSelection,
  serializeClipboardQuery,
  stringifyToken,
} from '../utils';

import { useQueryBuilderState } from './useQueryBuilderState';

/**
 * A FILTER token is "empty" when its value has no meaningful content —
 * `field:=:` produces `value: ''`, an empty multi-select produces `value: []`.
 * Numbers and booleans are never empty (`0` / `false` are legitimate values).
 */
function isEmptyFilterValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === 'string') {
    return value === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

/**
 * Returns true when every token is "ready to auto-commit" — no FREE_TEXT
 * residue and no FILTER lozenges with empty values. Auto-commit paths use
 * this gate so that creating a fresh empty lozenge (e.g. picking a field
 * from FreeText autocomplete) doesn't commit before the user has supplied
 * a value. The popover-dismiss commit bypasses this gate so dismissing the
 * value cell autocomplete *does* commit the empty lozenge through to the
 * consumer.
 */
function isQueryAutoCommittable(tokens: ParsedToken[]): boolean {
  return tokens.every((token, index) => {
    if (token.type === 'FREE_TEXT') {
      return false;
    }
    if (token.type === 'FILTER') {
      return !isEmptyFilterValue(token.value);
    }
    if (token.type === 'EXPRESSION') {
      return isQueryAutoCommittable(token.inner);
    }
    if (token.type === 'LOGIC_NOT') {
      const next = tokens[index + 1];
      return next?.type === 'FILTER' || next?.type === 'EXPRESSION';
    }
    return true;
  });
}

export interface UseQueryBuilderSetupOptions {
  value?: string;
  defaultValue?: string;
  onChange?: (event: QueryBuilderChangeEvent) => void;
  /** Clipboard serialization dialect. Defaults to Lezer (`field:=:value`). */
  clipboardDialect?: ClipboardDialect;
  /** Override pasted-text normalization. Defaults to Lezer-or-ANTLR detection. */
  parseClipboard?: (text: string) => string;
}

export interface UseQueryBuilderSetupResult {
  contextValue: QueryBuilderContextValue;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

export function useQueryBuilderSetup({
  value,
  defaultValue,
  onChange,
  clipboardDialect = 'lezer',
  parseClipboard: parseClipboardProp,
}: UseQueryBuilderSetupOptions): UseQueryBuilderSetupResult {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { state, dispatch } = useQueryBuilderState(value ?? defaultValue ?? '');
  const [activePopover, setActivePopoverState] = useState<ActivePopover>(null);
  const activePopoverRef = useRef<ActivePopover>(null);
  const setActivePopover = useCallback((popover: ActivePopover) => {
    activePopoverRef.current = popover;
    setActivePopoverState(popover);
  }, []);
  const [activeValueField, setActiveValueField] = useState<string | undefined>();
  const [activeValueSearch, setActiveValueSearch] = useState<string | undefined>();

  const parsedQuery = useMemo(() => parseQuery(state.query), [state.query]);
  const parsedCommittedQuery = useMemo(() => parseQuery(state.committedQuery), [state.committedQuery]);

  const formatClipboard = useCallback(
    (query: string) => serializeClipboardQuery(query, clipboardDialect),
    [clipboardDialect],
  );
  const parseClipboard = useCallback(
    (text: string) => (parseClipboardProp ?? parseClipboardQuery)(text),
    [parseClipboardProp],
  );

  const onChangeRef = useRef(onChange);
  const stateRef = useRef(state);
  const lastCommittedRef = useRef(state.committedQuery);
  useLayoutEffect(() => {
    onChangeRef.current = onChange;
    stateRef.current = state;
    lastCommittedRef.current = state.committedQuery;
  });

  // Sync controlled value changes into internal state (external system → internal state).
  // Mirror the auto-commit branch in `contextDispatch`: if the externally-set query
  // is fully parsed (no FREE_TEXT residue), commit it so `committedQuery` and
  // `parsedCommittedQuery` track external changes the same way they track internal
  // edits. We dispatch directly (not via `contextDispatch`) so we don't echo
  // onChange back to the consumer that just supplied this `value`.
  const prevValueRef = useRef(value);
  useEffect(() => {
    if (value === undefined || value === prevValueRef.current) {
      return;
    }
    prevValueRef.current = value;
    // Already our own query: the consumer is echoing back the commit we just made.
    if (value === stateRef.current.query) {
      return;
    }
    dispatch({ type: 'SET_QUERY', query: value });
    const tokens = parseQuery(value);
    if (isQueryAutoCommittable(tokens)) {
      dispatch({ type: 'COMMIT_QUERY' });
      lastCommittedRef.current = value;
    }
  }, [value, dispatch]);

  const contextDispatch = useCallback(
    (action: Parameters<typeof dispatch>[0]) => {
      const queryToCommit = action.type === 'COMMIT_QUERY' ? (action.query ?? stateRef.current.query) : null;
      const forceCommit = action.type === 'COMMIT_QUERY' && action.force;
      // A forced commit (Enter) bypasses the auto-committable gate: the user
      // explicitly asked to re-run the search, so an in-progress query (FREE_TEXT
      // residue or an empty FILTER value) must still notify the consumer rather
      // than being silently dropped here.
      if (queryToCommit !== null && !forceCommit && !isQueryAutoCommittable(parseQuery(queryToCommit))) {
        return;
      }

      // Capture pre-dispatch values where the notification needs them. A forced
      // commit (Enter) notifies even when the query is unchanged, so the search
      // re-runs on the already-committed query.
      const commitNotification =
        queryToCommit !== null && (forceCommit || queryToCommit !== lastCommittedRef.current) ? queryToCommit : null;

      dispatch(action);

      if (commitNotification !== null) {
        lastCommittedRef.current = commitNotification;
        onChangeRef.current?.({ query: commitNotification, tokens: parseQuery(commitNotification) });
      }

      if (action.type === 'UPDATE_QUERY') {
        lastCommittedRef.current = action.query;
        onChangeRef.current?.({ query: action.query, tokens: parseQuery(action.query) });
      }

      if (action.type === 'SET_QUERY') {
        const tokens = parseQuery(action.query);
        if (isQueryAutoCommittable(tokens) && action.query !== lastCommittedRef.current) {
          dispatch({ type: 'COMMIT_QUERY' });
          lastCommittedRef.current = action.query;
          onChangeRef.current?.({ query: action.query, tokens });
        }
      }

      // Removing a lozenge via the remove button is a complete, deliberate user
      // action — commit immediately rather than waiting for FilterBar.onBlur to
      // fire on the next FreeText focus. We recompute the post-deletion query
      // independently (the same `removeToken` util the reducer uses) since
      // `stateRef.current` won't reflect the dispatch above until the next render.
      if (action.type === 'DELETE_TOKEN') {
        let newQuery = removeToken(stateRef.current.query, action.token);
        let tokens = parseQuery(newQuery);
        // Deleting the filter that a standalone NOT applied to leaves `NOT`
        // (and often an orphan AND/OR), which is incomplete for auto-commit.
        // Strip dangling NOT(s) and extra LOGIC_BOOL connectors so we commit
        // the remaining query (including empty) instead of leaving the
        // consumer on the deleted negated filter or a malformed `a AND AND c`.
        if (!isQueryAutoCommittable(tokens)) {
          const cleanedTokens = dropDanglingLogicNots(tokens);
          const cleanedQuery = cleanedTokens.map(stringifyToken).join(' ');
          if (cleanedQuery !== newQuery && isQueryAutoCommittable(cleanedTokens)) {
            dispatch({ type: 'SET_QUERY', query: cleanedQuery });
            newQuery = cleanedQuery;
            tokens = cleanedTokens;
          }
        }
        if (isQueryAutoCommittable(tokens) && newQuery !== lastCommittedRef.current) {
          dispatch({ type: 'COMMIT_QUERY' });
          lastCommittedRef.current = newQuery;
          onChangeRef.current?.({ query: newQuery, tokens });
        }
      }

      // Paste / cut / type-over of a multi-token selection is a complete edit — commit
      // immediately rather than waiting for FilterBar.onBlur. Same stale-state reason as
      // DELETE_TOKEN: recompute with replaceSelection because stateRef lags this dispatch.
      if (action.type === 'REPLACE_SELECTION' && action.tokens.length > 0) {
        const newQuery = replaceSelection(stateRef.current.query, action.tokens, action.replacement);
        const tokens = parseQuery(newQuery);
        if (isQueryAutoCommittable(tokens) && newQuery !== lastCommittedRef.current) {
          dispatch({ type: 'COMMIT_QUERY' });
          lastCommittedRef.current = newQuery;
          onChangeRef.current?.({ query: newQuery, tokens });
        }
      }
    },
    [dispatch],
  );

  // Commit the current query whenever a lozenge cell popover dismisses (transition
  // from non-null → null). UPDATE_FILTER_VALUE and friends don't auto-commit, so
  // without this the chart / form consumer wouldn't see the finished edit until
  // something else triggered a commit. This bypasses contextDispatch's
  // empty-filter gate so dismissing the value cell autocomplete *does* commit
  // an empty `field:=:""` lozenge — that's exactly the moment the user signalled
  // they're done with this edit, even if the value is blank.
  //
  // useLayoutEffect (not useEffect) so the commit lands synchronously after the
  // DOM commit that flipped `activePopover` to null. Consumers that read
  // `committedQuery` from a layout-pass observer see the new committed value
  // in the same frame as the popover unmount, not a paint later.
  const fieldOwnersRef = useRef<Map<string, FieldOwner>>(new Map());
  const registerFieldOwner = useCallback((fieldKey: string, owner: FieldOwner) => {
    fieldOwnersRef.current.set(fieldKey, owner);
    return () => {
      if (fieldOwnersRef.current.get(fieldKey) === owner) {
        fieldOwnersRef.current.delete(fieldKey);
      }
    };
  }, []);
  const getFieldOwner = useCallback((fieldKey: string) => fieldOwnersRef.current.get(fieldKey), []);

  const suppressNextPopoverCloseCommitRef = useRef(false);
  const suppressNextPopoverCloseCommit = useCallback(() => {
    suppressNextPopoverCloseCommitRef.current = true;
  }, []);

  const prevActivePopoverRef = useRef(activePopover);
  useLayoutEffect(() => {
    const wasOpen = prevActivePopoverRef.current !== null;
    const nowClosed = activePopover === null;
    prevActivePopoverRef.current = activePopover;
    if (!wasOpen || !nowClosed) {
      return;
    }
    if (suppressNextPopoverCloseCommitRef.current) {
      suppressNextPopoverCloseCommitRef.current = false;
      return;
    }
    if (stateRef.current.query !== lastCommittedRef.current) {
      const committed = stateRef.current.query;
      dispatch({ type: 'COMMIT_QUERY' });
      lastCommittedRef.current = committed;
      onChangeRef.current?.({ query: committed, tokens: parseQuery(committed) });
    }
  }, [activePopover, dispatch]);

  return {
    wrapperRef,
    contextValue: {
      query: state.query,
      committedQuery: state.committedQuery,
      parsedQuery,
      parsedCommittedQuery,
      parseQuery,
      dispatch: contextDispatch,
      focusOverride: state.focusOverride,
      wrapperRef,
      activePopover,
      setActivePopover,
      activePopoverRef,
      activeValueField,
      setActiveValueField,
      activeValueSearch,
      setActiveValueSearch,
      registerFieldOwner,
      getFieldOwner,
      formatClipboard,
      parseClipboard,
      suppressNextPopoverCloseCommit,
    },
  };
}
